import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import PlusIcon from './icons/PlusIcon';
import ListIcon from './icons/ListIcon';
import { usePageMeta } from '../context/PageMeta';
import formatRelativeTime from '../utils/formatRelativeTime';
import { formatOrderDateTime } from '../utils/formatOrderDate';
import { getTelemetryPage, track } from '../telemetry';

// Auto-refresh cadence. Off on every load; the toggle lives in the top bar.
const AUTO_REFRESH_MS = 3 * 60_000;

export interface PrimaryAction {
  label: string;
  onClick: () => void;
}

interface IProps {
  title: string;
  noun?: string;
  showMenuButton: boolean;
  onToggleSidebar: () => void;
  primaryAction?: PrimaryAction;
  showExport?: boolean;
  // The "Updated … ago" text and the auto-refresh switch: the order list pages only.
  showRefresh?: boolean;
}

const TopBar: React.FC<IProps> = ({
  title,
  noun,
  showMenuButton,
  onToggleSidebar,
  primaryAction,
  showExport = false,
  showRefresh = false,
}) => {
  const { meta } = usePageMeta();
  // Re-render every half minute so "5 minutes ago" keeps counting.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!meta.updatedAt) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [meta.updatedAt]);

  // Auto-refresh: re-run the page's list query (and the sidebar counts) every 3 minutes.
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const { refresh } = meta;
  useEffect(() => {
    if (!autoRefresh || !refresh) return undefined;
    const timer = setInterval(() => {
      track('auto_refresh_fired', { page: getTelemetryPage() });
      refresh();
      queryClient.invalidateQueries(['order-count']);
      queryClient.invalidateQueries(['order-summary']);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, refresh, queryClient]);
  const subtitle =
    noun && typeof meta.total === 'number'
      ? `${meta.total} ${noun}${meta.total === 1 ? '' : 's'}`
      : null;

  return (
    <header className="topbar">
      {showMenuButton ? (
        <button
          type="button"
          className="icon-btn"
          aria-label="Toggle navigation"
          onClick={onToggleSidebar}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      ) : null}
      <div className="topbar-title">
        <h1>{title}</h1>
        {subtitle ? <div className="topbar-sub">{subtitle}</div> : null}
      </div>
      <div className="topbar-actions">
        {showRefresh && meta.updatedAt ? (
          <span
            className="topbar-updated"
            title={`Updated ${formatOrderDateTime(
              new Date(meta.updatedAt).toISOString(),
            )}`}
          >
            Updated {formatRelativeTime(meta.updatedAt, now)}
          </span>
        ) : null}
        {showRefresh && refresh ? (
          <label
            htmlFor="topbar-auto-refresh"
            className={`topbar-auto${autoRefresh ? ' is-on' : ''}`}
            title={
              autoRefresh
                ? 'Refreshing every 3 minutes. Click to stop.'
                : 'Refresh this list every 3 minutes'
            }
          >
            <input
              id="topbar-auto-refresh"
              type="checkbox"
              role="switch"
              checked={autoRefresh}
              onChange={(e) => {
                track('auto_refresh_toggled', { enabled: e.target.checked });
                setAutoRefresh(e.target.checked);
              }}
              data-track="auto_refresh"
            />
            <span className="topbar-switch" aria-hidden="true" />
            Auto-refresh
          </label>
        ) : null}
        {showExport ? (
          <Dropdown align="end" className="topbar-export">
            <Dropdown.Toggle
              variant="outline-secondary"
              className="btn-app btn-app--outline"
            >
              <ListIcon /> Export
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.ItemText className="topbar-export-note">
                Tell me what you need.
              </Dropdown.ItemText>
            </Dropdown.Menu>
          </Dropdown>
        ) : null}
        {primaryAction ? (
          <button
            type="button"
            className="btn-app btn-app--primary"
            onClick={primaryAction.onClick}
          >
            <PlusIcon />
            {primaryAction.label}
          </button>
        ) : null}
      </div>
    </header>
  );
};

export default TopBar;
